import { useState } from 'react';
import { Anchor, AspectRatio, Box, Card, Text, ThemeIcon } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'is-mobile';
import {
  PRODUCT_CARD_PREVIEW_BG,
  PRODUCT_CARD_PREVIEW_IMAGE_HEIGHT,
  productCardPreviewBackground,
} from '@/constants/productPreview';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import classes from './CreateProductCard.module.css';

type CreateProductCardProps = {
  href: string;
  corporateColor?: string;
};

export function CreateProductCard({
  href,
  corporateColor = '#1c7ed6',
}: CreateProductCardProps) {
  const { t } = useTranslation();
  const mobile = isMobile();
  const [hovered, setHovered] = useState(false);

  const borderColor = hovered ? corporateColor : colorWithAlpha(corporateColor, 0.45);
  const previewHeight = mobile
    ? PRODUCT_CARD_PREVIEW_IMAGE_HEIGHT.mobile
    : PRODUCT_CARD_PREVIEW_IMAGE_HEIGHT.desktop;

  return (
    <Anchor
      href={href}
      underline="never"
      style={{ textDecoration: 'none', display: 'block', width: '100%', color: 'inherit' }}
    >
      <Card
        shadow={hovered ? 'md' : 'sm'}
        padding={0}
        radius="md"
        withBorder
        className={`${classes.card} ${mobile ? classes.cardMobile : ''}`}
        style={{
          borderColor,
          backgroundColor: hovered
            ? 'var(--mantine-color-default)'
            : PRODUCT_CARD_PREVIEW_BG,
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Card.Section>
          <AspectRatio ratio={1} style={{ height: previewHeight }}>
            <div
              className={classes.preview}
              style={{
                background: productCardPreviewBackground(corporateColor, colorWithAlpha),
              }}
            >
              <div className={classes.previewPattern} />
              <div
                className={classes.iconRing}
                style={{
                  width: mobile ? 56 : 64,
                  height: mobile ? 56 : 64,
                  backgroundColor: colorWithAlpha(corporateColor, hovered ? 0.18 : 0.12),
                  boxShadow: hovered
                    ? `0 0 0 8px ${colorWithAlpha(corporateColor, 0.08)}`
                    : `0 0 0 6px ${colorWithAlpha(corporateColor, 0.05)}`,
                }}
              >
                <ThemeIcon
                  size={mobile ? 34 : 40}
                  radius="xl"
                  variant="filled"
                  style={{ backgroundColor: corporateColor }}
                >
                  <IconPlus size={mobile ? 20 : 22} stroke={2.5} />
                </ThemeIcon>
              </div>
            </div>
          </AspectRatio>
        </Card.Section>

        <Box px="md" py="sm" className={classes.footer}>
          <Text fw={600} size="sm" lineClamp={1}>
            {t('dashboard.actions.createProductTitle')}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={2}>
            {t('dashboard.actions.createProductHint')}
          </Text>
        </Box>
      </Card>
    </Anchor>
  );
}
